import { lazy, type ComponentType } from 'react';
import { beginRouteLoad, endRouteLoad } from '@/lib/pageLoadSignal';
import type { RouteObject } from 'react-router-dom';
import AuthGuard from '../components/feature/AuthGuard';
import AdminAccessGuard from '../components/feature/AdminAccessGuard';
import DenyRolesGuard from '../components/feature/DenyRolesGuard';
import ItStaffGuard from '../components/feature/ItStaffGuard';
import ItDeveloperGuard from '../components/feature/ItDeveloperGuard';

/**
 * React.lazy をラップし、遅延読み込み（コード分割）したチャンクの取得中を
 * 共通シグナル（pageLoadSignal）へ記録する。
 *
 * 通信が遅い環境では、ページ移動時にそのページの JS チャンクを取得するだけで
 * 時間がかかる。その間はまだページの中身が描画されず Supabase へのデータ取得も
 * 始まっていないため、何も記録しないとページ遷移の白フェードが「読み込み完了」と
 * 誤判定してコンテンツより先に明けてしまう。取得中はカウントを立てることで、
 * フェードがコンテンツのロードを待つようにする。
 *
 * 同じページへ再度移動したときは React.lazy が結果をキャッシュし、factory は
 * 再実行されないため、二重カウントの心配はない（tracked フラグでさらに担保する）。
 */
function lazyRoute(factory: () => Promise<{ default: ComponentType }>) {
  let tracked = false;
  return lazy(() => {
    if (!tracked) {
      tracked = true;
      beginRouteLoad();
    }
    return factory().then(
      (mod) => {
        endRouteLoad();
        return mod;
      },
      (err) => {
        endRouteLoad();
        throw err;
      },
    );
  });
}

const Home = lazyRoute(() => import('../pages/home/page'));
const Login = lazyRoute(() => import('../pages/login/page'));
const UserDashboard = lazyRoute(() => import('../pages/user/dashboard/page'));
const AdminDashboard = lazyRoute(() => import('../pages/admin/dashboard/page'));
const UserAttendance = lazyRoute(() => import('../pages/user/attendance/page'));
const AdminAttendance = lazyRoute(() => import('../pages/admin/attendance/page'));
const UserWork = lazyRoute(() => import('../pages/user/work/page'));
const AdminWork = lazyRoute(() => import('../pages/admin/work/page'));
const UserAnnouncements = lazyRoute(() => import('../pages/user/announcements/page'));
const AdminAnnouncements = lazyRoute(() => import('../pages/admin/announcements/page'));
const UserSupport = lazyRoute(() => import('../pages/user/support/page'));
const AdminSupport = lazyRoute(() => import('../pages/admin/support/page'));
const UserConsultation = lazyRoute(() => import('../pages/user/consultation/page'));
const AdminConsultation = lazyRoute(() => import('../pages/admin/consultation/page'));
const UserManuals = lazyRoute(() => import('../pages/user/manuals/page'));
const AdminManuals = lazyRoute(() => import('../pages/admin/manuals/page'));
const UserLinkShare = lazyRoute(() => import('../pages/user/link-share/page'));
const AdminLinkShare = lazyRoute(() => import('../pages/admin/link-share/page'));
const AdminUsers = lazyRoute(() => import('../pages/admin/users/page'));
const AdminOrganizations = lazyRoute(() => import('../pages/admin/organizations/page'));
const AdminReports = lazyRoute(() => import('../pages/admin/reports/page'));
const AdminBusinessCards = lazyRoute(() => import('../pages/admin/business-cards/page'));
const AdminSalesReports = lazyRoute(() => import('../pages/admin/sales-reports/page'));
const AdminSchedule = lazyRoute(() => import('../pages/admin/schedule/page'));
const AdminMessages = lazyRoute(() => import('../pages/admin/messages/page'));
const AdminNotifications = lazyRoute(() => import('../pages/admin/notifications/page'));
const AdminProjects = lazyRoute(() => import('../pages/admin/projects/page'));
const AdminDailyReports = lazyRoute(() => import('../pages/admin/daily-reports/page'));
const AdminMorningMeetings = lazyRoute(() => import('../pages/admin/morning-meetings/page'));
const AdminOfficeCalendar = lazyRoute(() => import('../pages/admin/office-calendar/page'));
const AdminLeave = lazyRoute(() => import('../pages/admin/leave/page'));
const ItSupportPage = lazyRoute(() => import('../pages/it-support/page'));
const ItSupportAiPage = lazyRoute(() => import('../pages/it-support/ai/page'));
const ItSupportReportPage = lazyRoute(() => import('../pages/it-support/report/page'));
const AdminItSupport = lazyRoute(() => import('../pages/admin/it-support/page'));
const AdminSystemHealth = lazyRoute(() => import('../pages/admin/system-health/page'));
const AdminUserActionLogs = lazyRoute(() => import('../pages/admin/user-action-logs/page'));
const MeetingPage = lazyRoute(() => import('../pages/meeting/page'));
const MeetingRoomsPage = lazyRoute(() => import('../pages/meeting/rooms/page'));
const MeetingSummariesPage = lazyRoute(() => import('../pages/meeting/summaries/page'));
const MeetingRecordingsPage = lazyRoute(() => import('../pages/meeting/recordings/page'));
const MeetingMaterialsPage = lazyRoute(() => import('../pages/meeting/materials/page'));
const MeetingSearchPage = lazyRoute(() => import('../pages/meeting/search/page'));
const MendanPage = lazyRoute(() => import('../pages/mendan/page'));
const MendanSettingsPage = lazyRoute(() => import('../pages/mendan-settings/page'));
const NotFound = lazyRoute(() => import('../pages/NotFound'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    // 招待URLからの参加はゲスト（未ログイン）も入室できるよう AuthGuard の外に置く
    path: '/meeting/:id',
    element: <MeetingPage />,
  },
  {
    // ここから下はすべてログイン必須（AuthGuard が共通で認証チェック）
    element: <AuthGuard />,
    children: [
      {
        path: '/user/dashboard',
        element: <UserDashboard />,
      },
      // 現場機能（利用者側）は福祉サービスを受けない一般社員（general_user）からは遮断する
      {
        element: <DenyRolesGuard deniedRoles={['general_user']} redirectTo="/user/dashboard" />,
        children: [
          {
            path: '/user/attendance',
            element: <UserAttendance />,
          },
          {
            path: '/user/work',
            element: <UserWork />,
          },
          {
            path: '/user/support',
            element: <UserSupport />,
          },
          {
            path: '/user/consultation',
            element: <UserConsultation />,
          },
        ],
      },
      {
        path: '/user/announcements',
        element: <UserAnnouncements />,
      },
      {
        path: '/user/manuals',
        element: <UserManuals />,
      },
      // リンクシェア（閲覧）。全ロールが利用でき、自分に共有されたリンクを閲覧する。
      {
        path: '/user/link-share',
        element: <UserLinkShare />,
      },
      // ITサポート（オンライン申請）。全ロールが利用でき、自分の申請を作成・確認できる。
      {
        path: '/it-support',
        element: <ItSupportPage />,
      },
      // ITサポート（AI）。全ロールが利用でき、AI一次対応のチャットを行う（5ターンで直接連絡を開放）。
      {
        path: '/it-support/ai',
        element: <ItSupportAiPage />,
      },
      // 不具合報告・機能追加の要望（D-2）。全ロールが利用でき、IT開発者へ通知される。
      {
        path: '/it-support/report',
        element: <ItSupportReportPage />,
      },
      // IT専用ページ（受付一覧）。ITフラグ（IT管理者／IT開発者）保有者のみ。
      // ※ ITフラグはロール非依存で付与できるため、RoleGuard ではなく ItStaffGuard で保護する。
      {
        element: <ItStaffGuard redirectTo="/user/dashboard" />,
        children: [
          {
            path: '/admin/it-support',
            element: <AdminItSupport />,
          },
          // システム健康度（IT開発者向け）。管理ダッシュボードの専用メニューから開く。
          {
            path: '/admin/system-health',
            element: <AdminSystemHealth />,
          },
        ],
      },
      // ユーザーログ（IT開発者専用）。全ユーザーの操作履歴を閲覧できる。
      {
        element: <ItDeveloperGuard />,
        children: [
          {
            path: '/admin/user-action-logs',
            element: <AdminUserActionLogs />,
          },
        ],
      },
      // 管理エリアは AdminAccessGuard で保護。
      // 管理系ロール（職員 / 事業所管理者 / 福祉事業管轄 / 総合管理者）は全許可。
      // 利用者（welfare_user）は、個別に許可された管理メニューのページだけ開ける。
      {
        element: <AdminAccessGuard redirectTo="/user/dashboard" />,
        children: [
          {
            path: '/admin/dashboard',
            element: <AdminDashboard />,
          },
          // 現場機能（利用者支援の現場系）。福祉事業管轄・総合管理者も現場の作業を行うため、
          // 職員・事業所管理者と同様にアクセスできる（遮断しない）。
          {
            path: '/admin/attendance',
            element: <AdminAttendance />,
          },
          {
            path: '/admin/work',
            element: <AdminWork />,
          },
          {
            path: '/admin/support',
            element: <AdminSupport />,
          },
          {
            path: '/admin/consultation',
            element: <AdminConsultation />,
          },
          {
            path: '/admin/daily-reports',
            element: <AdminDailyReports />,
          },
          {
            path: '/admin/morning-meetings',
            element: <AdminMorningMeetings />,
          },
          {
            path: '/admin/announcements',
            element: <AdminAnnouncements />,
          },
          {
            path: '/admin/manuals',
            element: <AdminManuals />,
          },
          // リンクシェア管理（職員以上）。AdminAccessGuard 配下で保護する。
          {
            path: '/admin/link-share',
            element: <AdminLinkShare />,
          },
          // ユーザー管理・組織管理は職員には不可（事業所管理者以上）
          {
            element: <DenyRolesGuard deniedRoles={['welfare_staff']} redirectTo="/admin/dashboard" />,
            children: [
              {
                path: '/admin/users',
                element: <AdminUsers />,
              },
              {
                path: '/admin/organizations',
                element: <AdminOrganizations />,
              },
            ],
          },
          {
            path: '/admin/reports',
            element: <AdminReports />,
          },
          {
            path: '/admin/business-cards',
            element: <AdminBusinessCards />,
          },
          {
            path: '/admin/sales-reports',
            element: <AdminSalesReports />,
          },
          {
            path: '/admin/schedule',
            element: <AdminSchedule />,
          },
          {
            path: '/admin/messages',
            element: <AdminMessages />,
          },
          {
            path: '/admin/notifications',
            element: <AdminNotifications />,
          },
          {
            path: '/admin/projects',
            element: <AdminProjects />,
          },
          // 勤務カレンダー設定（職員以上が閲覧可。編集は事業所管理者以上）
          {
            path: '/admin/office-calendar',
            element: <AdminOfficeCalendar />,
          },
          // 休暇設定（職員以上）
          {
            path: '/admin/leave',
            element: <AdminLeave />,
          },
        ],
      },
      // 管理者・利用者共通の機能
      {
        path: '/meeting',
        element: <MeetingPage />,
      },
      {
        path: '/meeting/rooms',
        element: <MeetingRoomsPage />,
      },
      {
        path: '/meeting/summaries',
        element: <MeetingSummariesPage />,
      },
      {
        path: '/meeting/recordings',
        element: <MeetingRecordingsPage />,
      },
      {
        path: '/meeting/materials',
        element: <MeetingMaterialsPage />,
      },
      {
        path: '/meeting/search',
        element: <MeetingSearchPage />,
      },
      // 面談（現場機能）。福祉事業管轄・総合管理者も現場の作業を行うため、職員・事業所管理者と同様にアクセスできる。
      // 福祉サービスを受けない一般社員（general_user）からは遮断する（オンライン会議 /meeting は利用可）。
      {
        element: <DenyRolesGuard deniedRoles={['general_user']} redirectTo="/user/dashboard" />,
        children: [
          {
            path: '/mendan',
            element: <MendanPage />,
          },
          {
            path: '/mendan/:id',
            element: <MendanPage />,
          },
          {
            path: '/mendan_setting',
            element: <MendanSettingsPage />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;